// SPDX-License-Identifier: LicenseRef-Proprietary
import { useCallback, useRef, useEffect, useLayoutEffect } from 'react';

export type Viewport = {
  scale: number;
  tx: number;
  ty: number;
};

interface UseViewportOptions {
  storageKey?: string; // e.g., `viewport:${tree.root.id}` for tree-specific persistence
}

export function useViewport(options?: UseViewportOptions) {
  const { storageKey } = options || {};
  const ref = useRef<SVGGElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const vp = useRef<Viewport>({ scale: 1, tx: 0, ty: 0 });
  const raf = useRef<number | null>(null);
  const saveRaf = useRef<number | null>(null);

  const apply = useCallback(() => {
    const element = ref.current;
    if (!element) return;
    const { scale, tx, ty } = vp.current;
    element.setAttribute('transform', `translate(${tx},${ty}) scale(${scale})`);
    raf.current = null;
  }, []);

  const schedule = useCallback(() => {
    if (raf.current != null) return;
    raf.current = requestAnimationFrame(apply);
  }, [apply]);

  // Load from sessionStorage on mount if key provided
  useEffect(() => {
    if (!storageKey) return;
    try {
      const saved = sessionStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved) as Viewport;
        if (parsed.scale && typeof parsed.tx === 'number' && typeof parsed.ty === 'number') {
          vp.current = parsed;
          schedule();
        }
      }
    } catch (e) {
      // ignore invalid saved data
    }
  }, [storageKey, schedule]);

  const saveToStorage = useCallback(() => {
    if (!storageKey) return;
    if (saveRaf.current) cancelAnimationFrame(saveRaf.current);
    saveRaf.current = requestAnimationFrame(() => {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(vp.current));
      } catch (e) {
        // ignore storage errors
      }
      saveRaf.current = null;
    });
  }, [storageKey]);

  const set = useCallback((next: Partial<Viewport>) => {
    vp.current = { ...vp.current, ...next };
    schedule();
    saveToStorage();
  }, [schedule, saveToStorage]);

  const setAbsolute = useCallback((next: Viewport) => {
    vp.current = next;
    schedule();
    saveToStorage();
  }, [schedule, saveToStorage]);

  const zoomToPoint = useCallback((cx: number, cy: number, factor: number) => {
    const z = vp.current;
    const nextScale = Math.min(5, Math.max(0.1, z.scale * factor));
    const sx = (cx - z.tx) / z.scale;
    const sy = (cy - z.ty) / z.scale;
    vp.current = { scale: nextScale, tx: cx - sx * nextScale, ty: cy - sy * nextScale };
    schedule();
    saveToStorage();
  }, [schedule, saveToStorage]);

  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    const direction = Math.sign(e.deltaY);
    const step = 0.1; // 10%
    const zoomFactor = direction < 0 ? (1 + step) : (1 - step);
    zoomToPoint(cx, cy, zoomFactor);
  }, [zoomToPoint]);

  const fitToScreen = useCallback((dimensions: { width: number; height: number }) => {
    const svg = svgRef.current;
    if (!svg) return;
    try {
      const svgRect = svg.getBoundingClientRect();
      const treeWidth = dimensions.width;
      const treeHeight = dimensions.height;
      
      const scaleX = svgRect.width / treeWidth;
      const scaleY = svgRect.height / treeHeight;
      const scale = Math.min(scaleX, scaleY, 1) * 0.9; // 90% of available space
      
      const centerX = svgRect.width / 2;
      const centerY = svgRect.height / 2;
      const translateX = centerX - (treeWidth * scale) / 2;
      const translateY = centerY - (treeHeight * scale) / 2;
      
      setAbsolute({ scale, tx: translateX, ty: translateY });
    } catch (error) {
      console.error('Error fitting to screen:', error);
    }
  }, [setAbsolute]);

  const resetView = useCallback(() => {
    setAbsolute({ scale: 1, tx: 0, ty: 0 });
  }, [setAbsolute]);

  // Setup wheel event listener
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    svg.addEventListener('wheel', handleWheel, { passive: false });
    return () => svg.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  // Reapply transform after React updates
  useLayoutEffect(() => {
    apply();
  });

  return {
    nodeRef: ref,
    svgRef,
    get: () => vp.current,
    set,
    setAbsolute,
    zoomToPoint,
    reset: resetView,
    resetView,
    fitToScreen,
    handleWheel,
  };
}
